import RiQuestionLine from '@ben/icons/question-line';
import { Checkbox as AriaCheckbox, useCheckboxState } from 'ariakit/checkbox';
import type { ChangeEvent, FocusEventHandler, ReactNode } from 'react';
import { forwardRef, useCallback, useId } from 'react';
import type { DefaultTheme, StyledComponent } from 'styled-components';
import styled, { css, useTheme } from 'styled-components';
import { variant } from 'styled-system';
import { TypographyVariant } from '../../utils/theme';
import RIcon from '../remix-icon';
import { NUMERIC_SIZES } from '../remix-icon/remix-icon';
import Tooltip from '../tooltip';
import Typography from '../typography/typography';

export interface ISwitchProps {
  label: string;
  labelVariant?: TypographyVariant;
  labelPosition?: 'left' | 'right';
  variant?: SwitchVariant;
  showLabel?: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  onBlur?: FocusEventHandler;
  defaultValue: boolean;
  hintText?: ReactNode;
  className?: string;
}

export enum SwitchVariant {
  Default = 'default',
  Active = 'active',
  Lock = 'lock',
}

export const Switch = forwardRef(
  (
    {
      label,
      labelVariant = TypographyVariant.bodyLg1,
      labelPosition = 'right',
      showLabel = true,
      variant = SwitchVariant.Default,
      onChange,
      defaultValue,
      hintText,
      className,
      ...rest
    }: ISwitchProps,
    ref
  ) => {
    const id = useId();
    const checkbox = useCheckboxState();

    const theme = useTheme();

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.checked);
      },
      [onChange]
    );

    return (
      <CheckBoxWrapper className={className}>
        {showLabel && labelPosition === 'left' && (
          <Typography variant={labelVariant}>{label}</Typography>
        )}
        <CheckBox
          ref={ref}
          id={id}
          state={checkbox}
          {...rest}
          variant={variant}
          onChange={handleChange}
          checked={defaultValue}
          aria-label={label}
          data-focus-visible={true}
        />
        <CheckBoxLabel
          htmlFor={id}
          labelPosition={labelPosition}
          variant={variant}
          disabled={rest.disabled}
        />
        <LabelWrapper>
          {showLabel && labelPosition === 'right' && (
            <Typography variant={labelVariant}>{label}</Typography>
          )}
          {hintText && (
            <Tooltip
              tooltipText={hintText}
              id={id}
              tooltipAnchorStyle={{
                height: `${NUMERIC_SIZES.xs}px`,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <RIcon icon={RiQuestionLine} color={theme.palette.neutral[80]} />
            </Tooltip>
          )}
        </LabelWrapper>
      </CheckBoxWrapper>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;

const CheckBoxWrapper: StyledComponent<
  'div',
  DefaultTheme,
  Record<string, unknown>,
  never
> = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  & > p {
    margin: 0;
  }
`;

const baseCheckboxStyles = ({
  labelPosition,
  disabled,
}: {
  labelPosition: string;
  disabled: boolean;
}) => css`
  top: 0;
  left: 0;
  width: 46px;
  height: 24px;
  border-radius: 40px;
  background: ${({ theme }) =>
    disabled ? theme.palette.neutral[50] : theme.palette.neutral[70]};
  background-repeat: no-repeat;
  background-position: right center;
  cursor: pointer;
  margin-left: ${labelPosition === 'left' ? '8px' : '0'};
  margin-right: ${labelPosition === 'right' ? '8px' : '0'};

  &::after {
    content: '';
    display: block;
    border-radius: ${({ theme }) => theme.borderRadius.full};
    width: 18px;
    height: 18px;
    margin: 3px;
    background: ${({ theme }) =>
      disabled ? theme.palette.neutral[60] : theme.palette.neutral['00']};
    transition: 0.2s;
  }
`;

const CheckBoxLabel: StyledComponent<
  'label',
  DefaultTheme,
  Record<string, unknown>,
  never
> = styled('label')(baseCheckboxStyles);

const baseStyle = () => css`
  opacity: 0;
  z-index: 1;
  width: 0px;
  height: 24px;
  position: absolute;
  left: -990px;
  &:checked + ${CheckBoxLabel} {
    &::after {
      content: '';
      display: block;
      border-radius: ${({ theme }) => theme.borderRadius.full};
      width: 18px;
      height: 18px;
      margin-left: 25px;
      transition: 0.2s;
    }
  }

  &:focus + ${CheckBoxLabel} {
    box-shadow: 0 0 2px 1px ${({ theme }) => theme.palette.neutral[100]};
    outline: 2px solid ${({ theme }) => theme.palette.focus};
    outline-offset: 2px;
  }
`;

const variantStyle = ({ theme }: { theme: DefaultTheme }) =>
  variant({
    variants: {
      [SwitchVariant.Default]: {
        [`&:checked + ${CheckBoxLabel}`]: {
          bg: theme.palette.primary.main,
        },
        [`&:disabled + ${CheckBoxLabel}`]: {
          cursor: 'not-allowed',
          bg: theme.palette.neutral[70],
        },
      },
      [SwitchVariant.Active]: {
        [`&:checked + ${CheckBoxLabel}`]: {
          bg: theme.palette.success.main,
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 3.5L4 6.5L9.5 1" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '8px center',
        },
        [`&:disabled + ${CheckBoxLabel}`]: {
          cursor: 'not-allowed',
          bg: theme.palette.neutral[50],
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 3.5L4 6.5L9.5 1" stroke="${encodeURIComponent(theme.palette.neutral[70])}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '8px center',
        },
        [`& + ${CheckBoxLabel}`]: {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g>
          <rect x="13.8488" y="12.6365" width="1.71429" height="12" rx="0.857143" transform="rotate(135 13.8488 12.6365)" fill="white"/>
          <rect x="12.6366" y="4.15112" width="1.71429" height="12" rx="0.857143" transform="rotate(45 12.6366 4.15112)" fill="white"/>
          </g>
          </svg>
          ')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '25px center',
        },
      },
      [SwitchVariant.Lock]: {
        [`& + ${CheckBoxLabel}`]: {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.15 5.60499V2.81514C10.15 1.5825 9.2098 0.583252 8.05 0.583252H5.95C4.7902 0.583252 3.85 1.5825 3.85 2.81514V5.60499M1.75 7.2789C1.75 6.35443 2.45515 5.60499 3.325 5.60499H10.675C11.5448 5.60499 12.25 6.35443 12.25 7.2789V11.7427C12.25 12.6671 11.5448 13.4166 10.675 13.4166H3.325C2.45515 13.4166 1.75 12.6672 1.75 11.7427V7.2789Z" stroke="white" stroke-width="1.16667"/>
          <path d="M7 8.75V10.5" stroke="white" stroke-width="1.16667" stroke-linecap="round"/>
          </svg>
          ')`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '25px center',
        },
        [`&:checked + ${CheckBoxLabel}`]: {
          bg: theme.palette.success.main,
          backgroundImage: `url('data:image/svg+xml;utf8,<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.75 7.45825C1.75 6.56079 2.45515 5.83325 3.325 5.83325H10.675C11.5448 5.83325 12.25 6.56079 12.25 7.45825V11.7916C12.25 12.689 11.5448 13.4166 10.675 13.4166H3.325C2.45515 13.4166 1.75 12.689 1.75 11.7916V7.45825Z" stroke="white" stroke-width="1.16667"/>
          <path d="M2.91667 3.49992C2.91667 3.82208 3.17783 4.08325 3.5 4.08325C3.82217 4.08325 4.08333 3.82208 4.08333 3.49992H2.91667ZM5.83333 1.16659H8.16667V-8.14199e-05H5.83333V1.16659ZM9.91667 2.91659V5.83325H11.0833V2.91659H9.91667ZM4.08333 3.49992V2.91654H2.91667V3.49992H4.08333ZM8.16667 1.16659C9.13317 1.16659 9.91667 1.95009 9.91667 2.91659H11.0833C11.0833 1.30575 9.7775 -8.14199e-05 8.16667 -8.14199e-05V1.16659ZM5.83333 -8.14199e-05C4.22252 -8.14199e-05 2.91667 1.3057 2.91667 2.91654H4.08333C4.08333 1.95006 4.86682 1.16659 5.83333 1.16659V-8.14199e-05Z" fill="white"/>
          <path d="M7 8.75V10.5" stroke="white" stroke-width="1.16667" stroke-linecap="round"/>
          </svg>
          ')`,
          backgroundPosition: '8px center',
          backgroundRepeat: 'no-repeat',
        },
        [`&:disabled + ${CheckBoxLabel}`]: {
          cursor: 'not-allowed',
          bg: theme.palette.neutral[70],
        },
      },
    },
  });

const CheckBox: StyledComponent<
  typeof AriaCheckbox,
  DefaultTheme,
  Record<string, unknown>,
  never
> = styled(AriaCheckbox)(baseStyle, variantStyle);

const LabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    flex-direction: row;
    gap: ${theme.spacing(0.5)};
  `}
`;
